<script setup lang="ts">

import {onBeforeMount, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {useLaureateStore} from "@/stores/laureate.ts";
import SeparateFieldsInput from "@/components/fields/SeparateFieldsInput.vue";
import ApiHelper, {API_HELPERS} from "@/helpers/apiHelper.ts";
import axios from "axios";
import AuthService from "@/services/authService.ts";

const autService = new AuthService(),
    laureateStore = useLaureateStore(),
    { laureateInfo, isLoading, errorMessage } = storeToRefs(laureateStore),
    payload = ref<any>({}),
    responseMessage = ref(''),
    sendingForm = ref(false),
    formSent = ref(false),
    errors = ref<any>({}),
    backendErrors = ref<any>({});

const COLUMNS_NUMBER: Record<string, number> = {
    'fullWidth': 1,
    'twoColumns': 2,
};

const checkPostcode = (event: any) => {
    const code = event.target.value;
    const key = event.keyCode || event.charCode;

    if (event.target.value.length == 2) {
        if( key == 8 || key == 46 ){
        }
        else{
            event.target.value = (code + '-');
        }
    }
    if (event.target.value.indexOf('--') !== -1) {
        event.target.value =(code.replace('--','-'));
    }
}

const checkDate = (event: any) => {
    const splittedDateItems = event.target.value.split('-');
    if (splittedDateItems.length == 3) {
        if (splittedDateItems[0].length > 4) {
            splittedDateItems[0] = splittedDateItems[0].slice(0,4);
            event.target.value = splittedDateItems.join('-');
        }
    }
}

const checkPhone = (fieldName: string, event: any) => {
    const phone = event.target.value;
    const regex = /^(?:(?:(?:(?:\+|00)\d{2})?[ -]?(?:(?:\(0?\d{2}\))|(?:0?\d{2})))?[ -]?(?:\d{3}[- ]?\d{2}[- ]?\d{2}|\d{2}[- ]?\d{2}[- ]?\d{3}|\d{7})|(?:(?:(?:\+|00)\d{2})?[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}))$/;
    if (!regex.test(phone)) {
        errors.value[fieldName] = 'Podaj prawidłowy numer telefonu';
    } else {
        errors.value[fieldName] = '';
    }
}

const checkBankAccount = (obj: any) => {
    const bankAccount = obj.value;
    const regex = /^[0-9]*$/;
    if (!regex.test(bankAccount) || bankAccount.length != 26) {
        errors.value[obj.field_name] = 'Podaj prawidłowy numer konta';
    } else {
        errors.value[obj.field_name] = '';
    }
}

const submitForm = async () => {
    for (let formLayoutIndex in laureateInfo.value.prize_form?.template) {
        let formLayout = laureateInfo.value.prize_form?.template[formLayoutIndex];
        for (let fieldIndex in formLayout.data?.fields) {
            let field = formLayout.data?.fields[fieldIndex];
            if (field.is_required && (!payload.value[field.name] || payload.value[field.name] == '')) {
                errors.value[field.name] = 'To pole jest wymagane';
            }
        }
    }

    if (!Object.values(errors.value).some(x => x !== null && x !== '')) {
        const data = {
            'name': payload.value['name'],
            'surname': payload.value['surname'],
            'birth_date': payload.value['birth_date'],
            'payload': JSON.stringify(payload.value),
        };

        sendingForm.value = true;
        const token = await autService.getToken();
        await axios.put(ApiHelper.getLaureateSaveUrl(), data, {
            headers: {'X-Form-Token': token}
        })
            .then(response => {
                errors.value = {};
                backendErrors.value = {};
                responseMessage.value = '';
                sendingForm.value = false;

                if (response.status == 200) {
                    formSent.value = true;
                    responseMessage.value = 'Dziękujemy za przesłanie formularza.';
                } else {
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                }
                window.scrollTo(0,0);
            }).catch(error => {
                if (error.response?.status == 422 && error.response?.data?.errors) {
                    backendErrors.value = error.response.data.errors;
                    errors.value = {}
                } else {
                    backendErrors.value = {}
                    errors.value = {}
                    responseMessage.value = 'Podczas wysyłania formularza wystąpił błąd.';
                }

                sendingForm.value = false;
            });
    }
}

onBeforeMount(async () => {
    await laureateStore.getLaureateInfo();
})

watch(laureateInfo, (newVal) => {
    payload.value['email'] = laureateInfo.value?.email;
})
</script>

<template>
    <div class="container mx-auto pb-12">
        <h1 class="text-5xl  text-center py-12">Formularz<br/> Laureata</h1>
        <div v-if="isLoading" class="text-center">
            <div class="text-center m-auto loader"></div>
        </div>
        <template v-else-if="!formSent && Object.keys(laureateInfo).length !== 0 && !isLoading">
            <div v-show="responseMessage" class="message-box text-center text-kozel-brown text-sm">{{ responseMessage }}</div>
            <form class="pt-4" @submit.prevent="submitForm">
                <div class="flex flex-col items-center gap-8 contact-items pb-12 px-12">
                    <div v-for="formLayout in laureateInfo.prize_form?.template" class="w-full">
                        <div v-if="formLayout.data?.header" class="text-xl text-kozel-white py-4">{{ formLayout.data?.header }}</div>
                        <div class="w-full grid gap-8"
                             :class="{'lg:grid-cols-2': COLUMNS_NUMBER[formLayout.type] === 2}"
                        >
                            <div v-for="field in formLayout.data?.fields"
                                 class="flex flex-col gap-1"
                            >
                                <template v-if="field.type === 'checkbox'">
                                    <div class="flex gap-2">
                                        <input type="checkbox"
                                               class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                                               v-model="payload[field.name]"
                                               :required="field.is_required"
                                        />
                                        <label class="text-sm text-kozel-white" v-html="field.label"></label>
                                    </div>
                                </template>
                                <template v-else>
                                    <label class="text-sm text-kozel-white">{{ field.label }}</label>

                                    <input v-if="field.type === 'number' || field.type === 'range'" type="number"
                                           class="block w-full px-1 py-2.5"
                                           v-model="payload[field.name]"
                                           :min="field.min ?? null"
                                           :max="field.max ?? null"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                    />
                                    <input v-else-if="field.type === 'date'" type="date"
                                           class="block w-full px-1 py-2.5"
                                           v-model="payload[field.name]"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           @keyup.prevent="checkDate"
                                           min="1000-01-01"
                                           max="9999-12-31"
                                    />
                                    <input v-else-if="field.type === 'email'" type="email"
                                           class="block w-full px-1 py-2.5"
                                           v-model="payload[field.name]"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           :maxlength="field.max_char ?? 255"
                                    />
                                    <input v-else-if="field.type === 'zip_code'" type="text"
                                           class="postcode block w-full px-1 py-2.5"
                                           v-model="payload[field.name]"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           pattern="[0-9]{2}[\-][0-9]{3}"
                                           @keyup.prevent="checkPostcode"
                                    />
                                    <input v-else-if="field.type === 'phone'" type="tel"
                                           class="block w-full px-1 py-2.5"
                                           v-model="payload[field.name]"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           @focusout="checkPhone(field.name, $event)"
                                    />
                                    <select v-else-if="field.type === 'select'"
                                            class="block w-full px-1.5 py-3"
                                            v-model="payload[field.name]"
                                            :required="field.is_required"
                                    >
                                        <option value="">{{ field.placeholder }}</option>
                                        <option v-for="option in field.options" :value="option.value">{{ option.name }}</option>
                                    </select>
                                    <SeparateFieldsInput v-else-if="field.type === 'bank_account'"
                                                         :fields="26"
                                                         :field-name="field.name"
                                                         v-model="payload[field.name]"
                                                         :special-gaps-after="[2,6,10,14,18,22]"
                                                         @update:validateValue="checkBankAccount"
                                    />
                                    <input v-else type="text"
                                           class="block w-full px-1 py-2.5"
                                           v-model="payload[field.name]"
                                           :required="field.is_required"
                                           :placeholder="field.placeholder ?? ''"
                                           :maxlength="field.max_char ?? 255"
                                    />
                                </template>

                                <div v-show="(errors && errors[field.name]) || (backendErrors && backendErrors[field.name])" class="text-kozel-brown error-label">
                                    <template v-if="errors && errors[field.name]">
                                        {{ Array.isArray(errors[field.name]) ? errors[field.name][0] : errors[field.name] }}
                                    </template>
                                    <template v-else>
                                        {{ Array.isArray(backendErrors[field.name]) ? backendErrors[field.name][0] : backendErrors[field.name] }}
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col gap-4 pt-8">
                        <div class="regulations-items">Informacje o przetwarzaniu danych osobowych</div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_1"
                                   v-model="payload['declaration_of_adulthood']"
                                   class="w-4 h-4 bg-kozel-white shrink-0"
                                   required
                            />
                            <label for="regulation_1" class="text-kozel-brown">* Oświadczam, że mam ukończone 18 lat.</label>
                        </div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_2"
                                   v-model="payload['acceptance_of_the_regulations']"
                                   class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                                   required
                            />
                            <label for="regulation_2" class="text-kozel-brown">* Oświadczam, że <a class="text-kozel-white" href="/regulations.pdf" download>Regulamin</a> Konkursu "WYZWANIE KOZLA" jest mi znany i&nbsp;akceptuję jego treść.</label>
                        </div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_3"
                                   v-model="payload['declaration_of_fulfilment_regulations_conditions']"
                                   class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                                   required
                            />
                            <label for="regulation_3" class="text-kozel-brown">* Oświadczam, że spełniam warunki przewidziane w&nbsp;&#167;2 <a class="text-kozel-white" href="/regulations.pdf" download>Regulamin</a> Konkursu.</label>
                        </div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_4"
                                   v-model="payload['confirmation_of_data_processing_knowledge']"
                                   class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                                   required
                            />
                            <label for="regulation_4" class="text-kozel-brown">* Oświadczam, iż zostałem poinformowany o&nbsp;zasadach przetwarzania moich danych osobowych, określonych <a class="text-kozel-white" href="/regulations.pdf" download>Regulaminem</a> Konkursu "WYZWANIE KOZLA".</label>
                        </div>
                        <div class="flex gap-2">
                            <input type="checkbox"
                                   id="regulation_5"
                                   v-model="payload['data_processing_consent']"
                                   class="appearance-none w-4 h-4 bg-kozel-white shrink-0"
                            />
                            <label for="regulation_5" class="text-kozel-brown">
                                Wyrażam zgodę na przetwarzanie moich danych osobowych
                                (imię, nazwisko, adres e-mai) w celu otrzymywania
                                wiadomości o&nbsp;charakterze marketingowym, dotyczących
                                marek Kompanii Piwowarskiej&nbsp;S.A. z&nbsp;siedzibą w&nbsp;Poznaniu,
                                która jest administratorem danych osobowych
                                przetwarzanych w&nbsp;tym celu. Szczegóły dotyczące
                                przetwarzania danych osobowych znajdują się w&nbsp;Regulaminie.
                            </label>
                        </div>
                        <div class="regulations-items pt-6">*Oświadczenie jest obowiązkowe</div>
                    </div>
                </div>
                <button class="block text-2xl" :disabled="sendingForm">
                    <span v-if="!sendingForm">WYŚLIJ</span>
                    <span v-else class="animate__animated animate__fadeIn">WYSYŁANIE...</span>
                </button>
            </form>
        </template>
        <template v-else-if="!formSent && Object.keys(laureateInfo).length === 0 && !isLoading">
            <div class="text-center text-xl px-4">{{errorMessage}}</div>
        </template>
        <template v-else-if="formSent">
            <div class="text-center text-xl px-4">{{ responseMessage }}</div>
        </template>
    </div>
</template>

<style scoped>
h1 {
    font-family: "Kozel", serif;
}

input {
    background: var(--color-kozel-white);
    font-size: 0.8rem;
}

input::placeholder, label, input, .regulations-items, .message-box, .error-label {
    font-family: 'ApexSerif', 'serif';
    font-weight: 700;
}

input::placeholder {
    color: var(--color-kozel-sandy);
}

select {
    background: var(--color-kozel-white);
    width: 100%;
    font-size: 0.8rem;
}

.error-label {
    font-size: 0.6rem;
}

button {
    background: var(--color-kozel-brown);
    padding: 1rem 4rem 0.5rem;
}
</style>